/* Collection pagination follows the rendered card size, viewport and browser zoom.
 * Shopify's ordinary pagination remains the no-JS / failed-request fallback. */
(() => {
  if (customElements.get('ergoa-adaptive-grid')) return;

  const pageSizeFor = (columns, rowHeight, viewportHeight) =>
    columns * Math.max(4, Math.min(10, Math.ceil((viewportHeight * 2) / rowHeight)));
  const pageStartFor = (index, size, total) =>
    Math.floor(Math.max(0, Math.min(index, total - 1)) / size) * size;
  const pageNumbers = (current, last) => {
    const numbers = [...new Set([1, current - 1, current, current + 1, last])]
      .filter((n) => n > 0 && n <= last).sort((a, b) => a - b);
    return numbers.flatMap((n, i) => i && n - numbers[i - 1] > 1 ? [null, n] : [n]);
  };

  // Stable grouping across the COMPLETE filtered collection, before pagination.
  const availableFirst = (items) => {
    const available = [], soldOut = [], ids = new Set();
    for (const html of items) {
      const id = html.match(/data-product-id="(\d+)"/)?.[1];
      const stock = html.match(/data-product-available="(true|false)"/)?.[1];
      if (!id || !stock || ids.has(id)) throw new Error('Invalid collection stock identity');
      ids.add(id);
      (stock === 'true' ? available : soldOut).push(html);
    }
    return available.concat(soldOut);
  };

  class ErgoaAdaptiveGrid extends HTMLElement {
    connectedCallback() {
      // Facet responses replace this element: each result set gets its own cache.
      queueMicrotask(() => this.isConnected && !this.grid && this.initialize());
    }

    initialize() {
      this.grid = this.querySelector('#product-grid');
      if (!this.grid?.children.length) return;
      this.total = Number(this.dataset.total);
      this.batch = Number(this.dataset.batchSize);
      this.serverPage = Number(this.dataset.serverPage);
      if (!(this.total > 0 && this.batch > 0 && this.serverPage > 0)) return;
      this.baseURL = new URL(location.href);
      this.baseURL.hash = '';
      this.baseURL.searchParams.delete('section_id');
      this.baseURL.searchParams.delete('page');
      this.cache = new Map([[this.serverPage, Array.from(this.grid.children, (li) => li.outerHTML)]]);
      this.requests = new Map();
      this.abort = new AbortController();
      this.generation = 0;
      this.start = (this.serverPage - 1) * this.batch;
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.resize(), 220);
      };
      this.onHistory = () => {
        if (this.sameResults()) this.render(this.indexFromURL(), this.size, 'none');
      };
      this.onClick = (event) => {
        const link = event.target.closest('a[data-ergoa-start]');
        if (!link || !this.contains(link) || event.button !== 0 || event.ctrlKey ||
            event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.render(Number(link.dataset.ergoaStart), this.size, 'push', true, link.href);
      };
      this.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('popstate', this.onHistory);
      // Pinch zoom deliberately does not repaginate; desktop zoom changes CSS width.
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this.grid);
      this.resize(true);
      document.fonts?.ready.then(() => this.isConnected && this.resize(true));
    }

    disconnectedCallback() {
      clearTimeout(this.resizeTimer);
      this.generation++;
      this.abort?.abort();
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('popstate', this.onHistory);
      this.removeEventListener('click', this.onClick);
    }

    sameResults() {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.delete('page');
      return url.href === this.baseURL.href;
    }

    indexFromURL() {
      const match = location.hash.match(/^#ergoa-products-(\d+)$/);
      if (match) return Number(match[1]);
      return (Math.max(1, Number(new URL(location.href).searchParams.get('page')) || 1) - 1) * this.batch;
    }

    resize(force = false) {
      if (!this.grid?.isConnected || !this.sameResults()) return;
      const width = this.grid.clientWidth;
      const height = window.innerHeight;
      if (!width || (!force && width === this.lastWidth && height === this.lastHeight)) return;
      this.lastWidth = width;
      this.lastHeight = height;
      const style = getComputedStyle(this.grid);
      const gap = parseFloat(style.columnGap) || 0;
      const tracks = style.gridTemplateColumns;
      const columns = tracks && tracks !== 'none' ? tracks.split(' ').length :
        Math.max(1, Math.round((width + gap) / (this.grid.firstElementChild.offsetWidth + gap)));
      const cardHeight = Math.max(...Array.from(this.grid.children).slice(0, columns).map((li) => li.offsetHeight));
      const rowHeight = Math.max(1, cardHeight + (parseFloat(style.rowGap) || 0));
      const size = pageSizeFor(columns, rowHeight, height);
      const index = this.size ? this.start : this.indexFromURL();
      if (size !== this.size || !this.hasAttribute('data-ready')) this.render(index, size, 'replace');
    }

    async loadPage(page) {
      if (this.cache.has(page)) return this.cache.get(page);
      if (this.requests.has(page)) return this.requests.get(page);
      const url = new URL(this.baseURL);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('page', page);
      const request = (async () => {
        const response = await fetch(url, { signal: this.abort.signal });
        if (!response.ok) throw new Error(`Section response ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = parsed.querySelector('ergoa-adaptive-grid');
        if (!source || Number(source.dataset.serverPage) !== page || Number(source.dataset.total) !== this.total ||
            Number(source.dataset.batchSize) !== this.batch) throw new Error('Unexpected collection response');
        const items = Array.from(source.querySelector('#product-grid').children, (li) => li.outerHTML);
        if (items.length !== Math.min(this.batch, this.total - (page - 1) * this.batch)) {
          throw new Error('Incomplete collection response');
        }
        this.cache.set(page, items);
        return items;
      })();
      this.requests.set(page, request);
      try { return await request; } finally { this.requests.delete(page); }
    }

    async loadOrderedItems() {
      if (this.orderedItems) return this.orderedItems;
      if (this.orderRequest) return this.orderRequest;
      this.orderRequest = (async () => {
        const count = Math.ceil(this.total / this.batch);
        // Bounded concurrency; page requests reuse this result set's cache.
        let next = 1;
        const worker = async () => {
          while (next <= count) await this.loadPage(next++);
        };
        await Promise.all(Array.from({ length: Math.min(3, count) }, worker));
        const items = Array.from({ length: count }, (_, i) => this.cache.get(i + 1)).flat();
        if (items.length !== this.total) throw new Error('Incomplete stock ordering');
        // Keyboard families are already grouped on the server. Keep related
        // wired/wireless models together in the requested collection order.
        this.orderedItems = this.dataset.keyboardGroups === 'true' ? items : availableFirst(items);
        return this.orderedItems;
      })();
      try { return await this.orderRequest; }
      finally { this.orderRequest = null; }
    }

    urlFor(start) {
      const url = new URL(this.baseURL);
      const page = Math.floor(start / this.batch) + 1;
      if (page > 1) url.searchParams.set('page', page);
      // Native page stays useful without JS; the fragment keeps the exact offset.
      url.hash = start ? `ergoa-products-${start}` : '';
      return url;
    }

    async render(index, size, historyMode, scroll = false, fallbackURL = null) {
      if (!size || !this.sameResults()) return;
      const generation = ++this.generation;
      const start = pageStartFor(index, size, this.total);
      const end = Math.min(start + size, this.total);
      this.setAttribute('aria-busy', 'true');
      try {
        const ordered = await this.loadOrderedItems();
        if (generation !== this.generation || !this.isConnected || !this.sameResults()) return;
        const items = ordered.slice(start, end);
        if (items.length !== end - start) throw new Error('Missing products in adaptive page');
        this.grid.innerHTML = items.join('');
        this.grid.querySelectorAll('.scroll-trigger').forEach((item) => {
          item.classList.remove('scroll-trigger--offscreen');
          item.classList.add('scroll-trigger--cancel');
        });
        this.start = start;
        this.size = size;
        this.dataset.pageSize = size;
        this.dataset.start = start;
        this.setAttribute('data-ready', '');
        this.renderPagination();
        const collectionPage = this.closest('collection-component');
        collectionPage?.querySelectorAll('.ergoa-choice-guide, .ergoa-collection-copy__link').forEach((element) => {
          element.hidden = start > 0;
        });
        if (historyMode !== 'none') {
          const url = this.urlFor(start);
          // Preserve unrelated anchors on initial first-page loads.
          if (!start && historyMode === 'replace' && !location.hash.startsWith('#ergoa-products-')) url.hash = location.hash;
          const state = { ...history.state, searchParams: url.search.slice(1) };
          history[historyMode === 'push' ? 'pushState' : 'replaceState'](state, '', url);
        }
        if (scroll) {
          this.grid.setAttribute('tabindex', '-1');
          this.grid.focus({ preventScroll: true });
          this.grid.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && generation === this.generation) {
          // Keep the existing usable grid, or navigate to the native fallback page.
          console.warn('Ergoa: adaptive pagination retained its fallback.', error);
          if (fallbackURL) location.assign(fallbackURL);
        }
      } finally {
        if (generation === this.generation) this.removeAttribute('aria-busy');
      }
    }

    renderPagination() {
      this.querySelector('.pagination-wrapper')?.remove();
      const last = Math.ceil(this.total / this.size);
      if (last < 2) return;
      const current = Math.floor(this.start / this.size) + 1;
      const wrapper = document.createElement('div');
      wrapper.className = 'pagination-wrapper';
      wrapper.dataset.page = current;
      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('aria-label', this.dataset.paginationLabel);
      const list = document.createElement('ul');
      list.className = 'pagination__list list-unstyled';
      list.setAttribute('role', 'list');
      const appendLink = (page, direction) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'pagination__item link';
        if (direction) {
          link.classList.add('pagination__item-arrow', 'motion-reduce', direction === 'previous' ? 'pagination__item--next' : 'pagination__item--prev');
          link.append(this.querySelector('template[data-pagination-icon]').content.cloneNode(true));
          link.setAttribute('aria-label', direction === 'previous' ? this.dataset.previousLabel : this.dataset.nextLabel);
        } else {
          link.textContent = page;
          link.setAttribute('aria-label', this.dataset.pageLabel.replace('[page]', page));
        }
        if (page === current && !direction) {
          link.classList.add('pagination__item--current', 'light');
          link.setAttribute('aria-current', 'page');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('role', 'link');
        } else {
          const start = (page - 1) * this.size;
          link.href = this.urlFor(start);
          link.dataset.ergoaStart = start;
        }
        item.append(link);
        list.append(item);
      };
      if (current > 1) appendLink(current - 1, 'previous');
      for (const page of pageNumbers(current, last)) {
        if (page) appendLink(page);
        else {
          const item = document.createElement('li');
          const dots = document.createElement('span');
          dots.className = 'pagination__item';
          dots.textContent = '…';
          item.append(dots);
          list.append(item);
        }
      }
      if (current < last) appendLink(current + 1, 'next');
      nav.append(list);
      wrapper.append(nav);
      this.append(wrapper);
    }
  }
  customElements.define('ergoa-adaptive-grid', ErgoaAdaptiveGrid);
})();
